/**
 * El ancho REAL de la caja, en píxeles, para dibujar a escala 1:1.
 *
 * Un `viewBox` fijo de 640 dentro de una caja de 1050 no «se adapta»: escala
 * todo el dibujo ×1.64, y el texto va dentro del dibujo. Los rótulos del eje
 * pedían 10px en la hoja y salían a 16, la leyenda no cuadraba con ellos porque
 * es HTML y no escala, y el gráfico ocupaba 361px de alto donde el componente
 * declaraba 220. Se leía como «los gráficos son demasiado grandes», y era
 * exactamente eso, pero por escalado y no por diseño.
 *
 * Midiendo la caja y usando ese ancho como `viewBox`, la escala es 1:1: el
 * texto sale al tamaño que pide la hoja, el alto es el que declara el
 * componente, y el trazo mantiene su grosor a cualquier ancho de ventana.
 */

import { useLayoutEffect, useRef, useState } from "react";

export function useAncho(inicial = 640) {
  const caja = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(inicial);

  useLayoutEffect(() => {
    const nodo = caja.current;
    if (!nodo) return;
    // Se mide en el layout y no en un efecto diferido: con `useEffect` el
    // primer fotograma sale con el ancho de partida y el gráfico da un salto
    // visible al cargar la pantalla.
    const medir = (px: number) => {
      if (px > 0) setAncho(Math.round(px));
    };
    medir(nodo.getBoundingClientRect().width);
    const observador = new ResizeObserver(([entrada]) => medir(entrada.contentRect.width));
    observador.observe(nodo);
    return () => observador.disconnect();
  }, []);

  return [caja, ancho] as const;
}
